import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Phone, Clock, RefreshCw, Users, Stethoscope, Building2, Filter, ExternalLink, ArrowRightCircle, Loader2, Images, X, ChevronLeft, ChevronRight, ClipboardList, Smile, Edit3, Eye, Lock, Calendar as CalendarIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import PageHeader from "@/components/PageHeader";
import {
  Select, SelectContent, SelectItem, SelectTrigger,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "@/components/ui/sonner";
import { useAuth } from "@/contexts/AuthContext";
import { getStagePermission } from "@/utils/stagePermissions";

const API = window.location.origin + "/api";

const STUDIOS = ["Studio 1", "Studio 2", "Studio 3", "Studio 4", "Studio 5", "Studio 6", "Studio 7"];

// Mirrors REPARTI_CONFIG in App.js / OpportunitaCreatePage.jsx
const PIPELINE_TO_MODE = {
  // New pipelines (Feb-May 2026 migration)
  "529iRmd3Wh7bjdgfHpP9": "clinico",
  "Eu6Xz0GLvHfN5PejJ4SV": "ortodonzia",
  "I2wcBQKe3y6DhTYfFySx": "clinico",     // Promemoria - Reparto Clinico 01
  "l191W5Qj1vs4QSEc9xVd": "ortodonzia",  // Promemoria - Ortodonzia 01
  // Legacy
  "tmyDM7u0ygOPtn9rYMQT": "clinico",
  "cVQGQNimL45cGp3OBsAD": "clinico",
  "MyxgABpCKYCkMRBoQMDc": "ortodonzia",
  "T2q5AK85MGrZBv1z0nQy": "ortodonzia",
};

/**
 * Single-opportunity stage selector. Renders a small label with the
 * pipeline name + a Select with the current stage as label and only the
 * downstream stages as choices.
 */
function StageSelector({ opportunity, onConfirmed, patientName, showNoTransitBadge = true }) {
  const [pending, setPending] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [photosOpen, setPhotosOpen] = useState(false);
  const navigate = useNavigate();
  const { user: authUser } = useAuth();
  const isAdmin = (authUser?.role || "") === "admin";
  const isUserPlus = (authUser?.role || "") === "user_plus";

  // Stage selezionabili per il dropdown. Regola (24/06/2026):
  //   - admin / user_plus → tutti gli stage della pipeline tranne quello
  //     corrente (anche indietro per correggere errori)
  //   - user base       → solo stage con position > current (solo avanti)
  //
  // Computato direttamente da `pipeline_stages` + `current_stage_id` così
  // l'optimistic update dopo `handleStageMoved` ricalcola da solo le
  // opzioni corrette (niente race window di 1s con downstream_stages stale).
  // Fallback a `downstream_stages` per backward-compat con vecchie response.
  const availableStages = useMemo(() => {
    const fullList = opportunity.pipeline_stages;
    if (!Array.isArray(fullList) || fullList.length === 0) {
      // Fallback legacy: usa downstream_stages così com'è.
      return opportunity.downstream_stages || [];
    }
    const curId = opportunity.current_stage_id || "";
    const curPos = fullList.find((s) => s.id === curId)?.position;
    if (isAdmin || isUserPlus) {
      // Tutti tranne il corrente, ordinati per position
      return fullList
        .filter((s) => s.id !== curId)
        .sort((a, b) => (a.position || 0) - (b.position || 0))
        .map((s) => ({ id: s.id, name: s.name }));
    }
    // user base: solo stage con position > curPos (avanti)
    if (curPos == null) return [];
    return fullList
      .filter((s) => (s.position || 0) > curPos)
      .sort((a, b) => (a.position || 0) - (b.position || 0))
      .map((s) => ({ id: s.id, name: s.name }));
  }, [opportunity.pipeline_stages, opportunity.downstream_stages, opportunity.current_stage_id, isAdmin, isUserPlus]);

  const handleSelect = (stageId) => {
    if (!stageId) return;
    // Piano "ghost" ricostruito da opp_stage_history perché GHL l'ha
    // cancellato: non possiamo fare un PUT su un opp che GHL non
    // riconosce più (risponderebbe 404). Blocchiamo subito il flusso.
    if (opportunity.is_ghost_from_history) {
      toast.error(
        "Piano non più presente in GHL — non è possibile modificare lo stage. Ricrea il piano dal Kanban se necessario.",
        { duration: 6000 }
      );
      return;
    }
    const stage = availableStages.find((s) => s.id === stageId);
    if (!stage) return;
    setPending({
      stageId: stage.id,
      stageName: stage.name,
    });
  };

  const handleConfirm = async () => {
    if (!pending) return;
    setSubmitting(true);
    try {
      const { data } = await axios.post(
        `${API}/appointments/opportunities/${opportunity.id}/move-stage`,
        { stage_id: pending.stageId },
        { withCredentials: true }
      );
      // Quando GHL è giù la scrittura viene accodata e ritentata
      // automaticamente. Mostriamo un toast informativo invece del
      // successo standard per non confondere l'utente.
      if (data?.queued) {
        toast.success(
          `Spostato a "${pending.stageName}" — GHL lento, sincronizzazione in corso`,
          { duration: 5000, description: "L'operazione verrà ritentata automaticamente entro 60 secondi." }
        );
      } else {
        toast.success(`Spostato a "${pending.stageName}"`);
      }
      // Notify parent FIRST so the optimistic UI update happens before
      // we close the dialog — this way the user immediately sees the new
      // stage label on the row instead of waiting for the next refresh.
      onConfirmed?.(opportunity.id, pending.stageId, pending.stageName);
      setPending(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore durante lo spostamento");
    } finally {
      setSubmitting(false);
    }
  };

  const downstream = availableStages;
  // Even when there are no downstream stages (e.g. opp already in
  // "Completati"), we still render the block so the operator can see the
  // pipeline + current stage and use the edit/photo buttons.

  // "Muted" stages: opportunities the front-desk no longer needs to act on.
  // - "Completati" → Reparto Clinico / Indiretti (closed treatment plan)
  // - "Preventivi eseguiti" → Ortodonzia (estimate already delivered)
  // When the current stage matches, we render the whole stage block in a
  // muted grey style so it visually fades into the background of the row.
  const _curStageNorm = (opportunity.current_stage_name || "")
    .trim()
    .toLowerCase();
  const isMutedStage =
    _curStageNorm === "completati" ||
    _curStageNorm === "preventivi eseguiti" ||
    _curStageNorm === "indiretto completato" ||
    _curStageNorm === "indiretto completati";

  // "Seduta NON conteggiata come assicurato": il backend espone
  // `had_stage_transit_today` per ogni opp. Se false, l'opp è aperta ma
  // nel giorno selezionato non ha avuto alcun transito di stage
  // qualificante → non conta come "seduta con assicurazione" (stessa
  // regola del conteggio dashboard). Visibile SOLO se `showNoTransitBadge`
  // è true (default), che dipende dalla data selezionata e dall'ora:
  // giorno passato → sempre; oggi → solo dopo le 19; giorno futuro → mai.
  const noTransitToday = showNoTransitBadge && opportunity.had_stage_transit_today === false;
  const muted = isMutedStage || noTransitToday;

  // Permesso effettivo per questa opp basato sulla matrice stage_permissions
  // (admin sempre = tutto on; user = matrice o default solo-vision).
  // Chiama Paziente espone lo stage corrente come `current_stage_id`
  // (vedi routes_appointments.py:1542). Fallback ai vecchi nomi per
  // robustezza. Quando l'utente cambia stage via StageSelector questo
  // memo si ricomputa automaticamente perché `opportunity` viene
  // rimpiazzato con un nuovo oggetto contenente il nuovo `current_stage_id`.
  const currentStageId = opportunity.current_stage_id || opportunity.live_stage_id || opportunity.stage_id;
  const stagePerm = getStagePermission(authUser, opportunity.pipeline_id, currentStageId);
  const canEdit = stagePerm.edit;
  const canVision = stagePerm.vision || stagePerm.edit; // M implica V per accesso
  const canPhotoOnly = stagePerm.photo && !stagePerm.edit; // solo foto, resto locked

  // Chiama Paziente:
  //   - canEdit → apre in MODIFICA (pennino blu)
  //   - canVision (e non edit) → apre in SOLA CONSULTAZIONE con `&consult=1` (occhio)
  //   - nessun permesso → click mostra toast "Permesso negato" (lucchetto)
  // FOTO è gestito nel piano-cura: se canPhotoOnly attiva il photo-only mode.
  const handleEdit = () => {
    if (!canVision && !canEdit) {
      toast.error("Permesso negato per questo stage");
      return;
    }
    const mode = PIPELINE_TO_MODE[opportunity.pipeline_id] || "clinico";
    try { sessionStorage.setItem("chiamaReturnActive", "1"); } catch { /* ignore */ }
    const consult = canEdit ? "" : "&consult=1";
    navigate(`/piano-cura-${mode}?opportunityId=${encodeURIComponent(opportunity.id)}${consult}`);
  };

  return (
    <div
      className={`flex flex-col gap-0.5 w-full ${muted ? "opacity-70" : ""}`}
      data-testid={`stage-block-${opportunity.id}`}
    >
      <span
        className={`text-[9px] font-bold uppercase tracking-wider truncate flex items-center gap-1.5 ${
          muted ? "text-slate-300" : "text-slate-400"
        }`}
      >
        <span className="truncate">{opportunity.pipeline_name || "Pipeline"}</span>
        {noTransitToday && !isMutedStage && (
          <span
            className="inline-flex items-center px-1.5 py-0 rounded text-[9px] font-bold uppercase tracking-wide bg-slate-200 text-slate-600 normal-case"
            title="Lo stage dell'opportunità non è cambiato nel giorno selezionato: non conta come seduta con assicurazione."
            data-testid={`opp-no-transit-${opportunity.id}`}
          >
            Senza cambio stage
          </span>
        )}
      </span>
      {/* Grid: fixed-width date slot · flexible stage · value+doctor info ·
          fixed edit · fixed photo. Slots are always rendered (with empty
          content when absent) so all rows align column-by-column,
          regardless of whether the opportunity has a date or photos
          attached. */}
      <div className="grid grid-cols-[52px_minmax(0,1fr)_100px_36px_36px] items-center gap-1.5">
        <span
          className="text-[10px] font-semibold text-slate-500 tabular-nums whitespace-nowrap text-right"
          title={
            opportunity.data_inserimento
              ? `Data inserimento Piano di Cura: ${(opportunity.data_inserimento.split('-')[2] || '')}/${(opportunity.data_inserimento.split('-')[1] || '')}/${(opportunity.data_inserimento.split('-')[0] || '')}`
              : undefined
          }
          data-testid={`opp-created-${opportunity.id}`}
        >
          {(() => {
            const di = opportunity.data_inserimento;
            if (!di || !/^\d{4}-\d{2}-\d{2}/.test(di)) return "";
            const [y, m, d] = di.split("-");
            return `${d}/${m}/${y.slice(2)}`;
          })()}
        </span>
        <Select value="" onValueChange={handleSelect} disabled={downstream.length === 0}>
          <SelectTrigger
            className={`h-9 px-3 text-xs rounded-lg gap-2 w-full min-w-0 ${
              downstream.length === 0 || muted
                ? "border-slate-200 bg-slate-50 text-slate-500"
                : "border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100"
            }`}
            data-testid={`stage-selector-${opportunity.id}`}
            onClick={(e) => e.stopPropagation()}
            title={
              downstream.length === 0
                ? "Nessuno stage successivo disponibile"
                : undefined
            }
          >
            <ArrowRightCircle className="w-3 h-3 flex-shrink-0" />
            <span className="truncate font-semibold">
              {opportunity.current_stage_name || "—"}
            </span>
          </SelectTrigger>
          <SelectContent className="max-h-80">
            <div className="px-2 py-1 text-[10px] text-slate-500">
              Fase attuale: <strong className="text-slate-700">{opportunity.current_stage_name || "—"}</strong>
            </div>
            {downstream.map((s) => (
              <SelectItem key={s.id} value={s.id} className="text-xs">
                {s.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {/* Valore opportunità + medico titolare. Mostrato a fianco del
            dropdown dello stage. Se entrambi mancano renderiamo comunque la
            colonna vuota per mantenere l'allineamento delle righe. */}
        <div
          className="flex flex-col items-end justify-center leading-tight min-w-0 max-w-[100px]"
          data-testid={`opp-value-${opportunity.id}`}
        >
          <span
            className={`text-[11px] font-bold tabular-nums whitespace-nowrap ${
              muted ? "text-slate-400" : "text-slate-700"
            }`}
            title="Valore totale dell'opportunità"
          >
            {(() => {
              const v = Number(opportunity.monetary_value || 0);
              if (!v) return "—";
              try {
                return `€ ${v.toLocaleString("it-IT", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}`;
              } catch {
                return `€ ${v.toFixed(2)}`;
              }
            })()}
          </span>
          <span
            className="text-[9px] text-slate-500 truncate w-full text-right"
            title={opportunity.assigned_user_name || "Medico titolare non assegnato"}
          >
            {opportunity.assigned_user_name || "—"}
          </span>
        </div>
        {/* Bottone azione opportunità — icona dinamica basata sul permesso
              di stage (matrice stage_permissions, richiesta utente 06/06/2026):
              - canEdit → pennino blu (apre in MODIFICA)
              - canVision (no edit) → occhio grigio (apre in CONSULTAZIONE)
              - nessun permesso → lucchetto rosso, click → toast "Permesso negato"
              Gli admin hanno sempre canEdit=true. */}
        <button
          type="button"
          onClick={handleEdit}
          data-testid={canEdit ? `edit-opp-${opportunity.id}` : (canVision ? `view-opp-${opportunity.id}` : `locked-opp-${opportunity.id}`)}
          title={
            !canVision && !canEdit
              ? "Permesso negato per questo stage"
              : canEdit
                ? (opportunity.is_archived ? "Modifica piano di cura (presente in archivio)" : "Modifica piano di cura")
                : (opportunity.is_archived ? "Consulta piano di cura (presente in archivio)" : "Consulta piano di cura")
          }
          className={`inline-flex items-center justify-center w-9 h-9 rounded-lg border transition-colors ${
            !canVision && !canEdit
              ? "bg-red-50 text-red-700 hover:bg-red-100 border-red-200"
              : opportunity.is_archived
                ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-100 hover:text-emerald-800 border-emerald-200"
                : canEdit
                  ? "bg-blue-50 text-blue-700 hover:bg-blue-100 hover:text-blue-800 border-blue-200"
                  : "bg-slate-50 text-slate-600 hover:bg-slate-100 hover:text-slate-800 border-slate-200"
          }`}
        >
          {!canVision && !canEdit
            ? <Lock className="w-4 h-4" />
            : canEdit
              ? <Edit3 className="w-4 h-4" />
              : <Eye className="w-4 h-4" />}
        </button>
        {/* Photo slot — always present in the grid (so columns align across
            rows). The button itself only renders when the opportunity has
            at least one non-deleted attachment in the `immagini` field. */}
        {opportunity.has_photos ? (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setPhotosOpen(true); }}
            data-testid={`opp-photos-${opportunity.id}`}
            title="Vedi foto di questa opportunità"
            className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-amber-50 text-amber-700 hover:bg-amber-100 hover:text-amber-800 border border-amber-200 transition-colors"
          >
            <Images className="w-4 h-4" />
          </button>
        ) : (
          <span className="w-9 h-9 inline-block" aria-hidden="true" />
        )}
      </div>

      <PhotosLightbox
        open={photosOpen}
        onOpenChange={setPhotosOpen}
        opportunities={[opportunity]}
        patientName={patientName || opportunity.pipeline_name || ""}
      />

      <Dialog open={!!pending} onOpenChange={(o) => !o && setPending(null)}>
        <DialogContent data-testid="confirm-move-stage-dialog">
          <DialogHeader>
            <DialogTitle>Confermi spostamento?</DialogTitle>
            <DialogDescription className="pt-2 space-y-1 text-slate-700">
              <span className="block">
                Sposterai l&apos;opportunità{" "}
                {opportunity.name && <strong>«{opportunity.name}»</strong>}{" "}
                dalla fase <strong>{opportunity.current_stage_name || "—"}</strong> alla fase{" "}
                <strong className="text-indigo-700">{pending?.stageName}</strong>.
              </span>
              {opportunity.pipeline_name && (
                <span className="block text-xs text-slate-400">Pipeline: {opportunity.pipeline_name}</span>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setPending(null)} disabled={submitting} data-testid="confirm-move-cancel">
              Annulla
            </Button>
            <Button
              onClick={handleConfirm}
              disabled={submitting}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid="confirm-move-confirm"
            >
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ArrowRightCircle className="w-4 h-4 mr-2" />}
              Sposta
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PhotosLightbox({ open, onOpenChange, opportunities, patientName }) {
  const [loading, setLoading] = useState(false);
  const [photos, setPhotos] = useState([]); // [{ url, opportunityName }]
  const [activeIdx, setActiveIdx] = useState(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setPhotos([]);
      setActiveIdx(null);
      try {
        const results = await Promise.all(
          (opportunities || []).map(async (opp) => {
            try {
              const { data } = await axios.get(
                `${API}/appointments/opportunities/${opp.id}/photos`,
                { withCredentials: true }
              );
              return (data?.photos || []).map((url) => ({
                url,
                opportunityName: opp.name || opp.pipeline_name || "",
              }));
            } catch {
              return [];
            }
          })
        );
        if (cancelled) return;
        const flat = results.flat();
        // de-dup by URL
        const seen = new Set();
        const unique = flat.filter((p) => (seen.has(p.url) ? false : (seen.add(p.url), true)));
        setPhotos(unique);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [open, opportunities]);

  // keyboard nav for the zoom view
  useEffect(() => {
    if (activeIdx === null) return;
    const handler = (e) => {
      if (e.key === "Escape") setActiveIdx(null);
      else if (e.key === "ArrowRight") setActiveIdx((i) => (i + 1) % photos.length);
      else if (e.key === "ArrowLeft") setActiveIdx((i) => (i - 1 + photos.length) % photos.length);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [activeIdx, photos.length]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-[96vw] w-[96vw] sm:max-w-[90vw] max-h-[92vh] h-[92vh] p-0 overflow-hidden bg-slate-900 text-white border-0"
        data-testid="photos-lightbox"
      >
        <DialogHeader className="px-5 py-3 border-b border-white/10 flex flex-row items-center justify-between gap-3 space-y-0">
          <div className="min-w-0">
            <DialogTitle className="text-base font-semibold text-white truncate">
              Foto · {patientName || "Paziente"}
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-400 mt-0.5">
              {loading ? "Caricamento foto…" : `${photos.length} foto trovate`}
            </DialogDescription>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-auto p-4 sm:p-6">
          {loading ? (
            <div className="h-full flex items-center justify-center text-slate-400">
              <Loader2 className="w-6 h-6 animate-spin mr-2" /> Caricamento…
            </div>
          ) : photos.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-slate-400 gap-2">
              <Images className="w-10 h-10 text-slate-500" />
              <p>Nessuna foto associata a questa opportunità.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
              {photos.map((p, idx) => (
                <button
                  key={p.url}
                  type="button"
                  onClick={() => setActiveIdx(idx)}
                  className="group relative aspect-square overflow-hidden rounded-lg bg-slate-800 border border-white/10 hover:border-indigo-400 transition-colors"
                  data-testid={`photo-thumb-${idx}`}
                >
                  <img
                    src={p.url}
                    alt={`foto-${idx}`}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </button>
              ))}
            </div>
          )}
        </div>

        {activeIdx !== null && photos[activeIdx] && (
          <div
            className="absolute inset-0 z-50 bg-black/95 flex items-center justify-center"
            onClick={() => setActiveIdx(null)}
            data-testid="photo-zoom"
          >
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setActiveIdx(null); }}
              className="absolute top-3 right-3 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
              aria-label="Chiudi"
              data-testid="photo-zoom-close"
            >
              <X className="w-5 h-5" />
            </button>
            {photos.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setActiveIdx((activeIdx - 1 + photos.length) % photos.length); }}
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
                  aria-label="Precedente"
                >
                  <ChevronLeft className="w-6 h-6" />
                </button>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setActiveIdx((activeIdx + 1) % photos.length); }}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
                  aria-label="Successiva"
                >
                  <ChevronRight className="w-6 h-6" />
                </button>
              </>
            )}
            <img
              src={photos[activeIdx].url}
              alt={`foto-${activeIdx}`}
              className="max-w-[94vw] max-h-[88vh] object-contain"
              onClick={(e) => e.stopPropagation()}
            />
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-xs text-white/80 bg-black/40 px-3 py-1 rounded-full">
              {activeIdx + 1} / {photos.length}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/**
 * Anteprima READ-ONLY di un Piano di Cura. Mostrata quando l'opportunità
 * NON è modificabile (stage "Completati" / "Preventivi eseguiti" o
 * archiviata). L'operatore può comunque consultare integralmente il piano
 * (denti, trattamenti, prezzi, note, totali) senza poter modificare.
 *
 * Richiesta utente 04/06/2026: "dalla pagina chiama paziente deve essere
 * possibile vedere tutti i contenuti dei piani di cura assicurativi, e
 * sono modificabili con il pennino solo quelli concessi da vincoli già
 * precedentemente impostati".
 */
function PianoCuraPreviewDialog({ open, onOpenChange, opportunityId, patientName }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [doc, setDoc] = useState(null);

  useEffect(() => {
    if (!open || !opportunityId) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError("");
      setDoc(null);
      try {
        const { data } = await axios.get(
          `${API}/opportunities/created/${opportunityId}?include_deleted=1`,
          { withCredentials: true }
        );
        if (!cancelled) setDoc(data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || "Errore nel caricamento del piano di cura");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [open, opportunityId]);

  const piano = doc?.piano_cura || [];
  const total = Number(doc?.total_cost || 0);
  const totalBase = Number(doc?.total_cost_base || 0);

  const fmtEuro = (v) => {
    const n = Number(v || 0);
    try {
      return `€ ${n.toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    } catch {
      return `€ ${n.toFixed(2)}`;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-[92vw] sm:max-w-3xl max-h-[88vh] overflow-hidden flex flex-col p-0"
        data-testid="piano-cura-preview-dialog"
      >
        <DialogHeader className="px-5 py-4 border-b bg-slate-50">
          <DialogTitle className="text-base font-semibold text-slate-800 flex items-center gap-2">
            <Eye className="w-4 h-4 text-slate-500" />
            Piano di Cura · {patientName || doc?.contact_name || "Paziente"}
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500 pt-1">
            Solo lettura — questa opportunità è in uno stage non modificabile.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {loading && (
            <div className="flex items-center justify-center py-12 text-slate-500">
              <Loader2 className="w-6 h-6 animate-spin mr-2" /> Caricamento…
            </div>
          )}
          {error && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
              {error}
            </div>
          )}
          {!loading && !error && doc && (
            <>
              {/* Meta */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                <div className="bg-slate-50 rounded px-2 py-1.5">
                  <div className="text-[10px] uppercase text-slate-400 font-semibold">Pipeline</div>
                  <div className="text-slate-700 truncate" title={doc.pipeline_name}>{doc.pipeline_name || "—"}</div>
                </div>
                <div className="bg-slate-50 rounded px-2 py-1.5">
                  <div className="text-[10px] uppercase text-slate-400 font-semibold">Stage</div>
                  <div className="text-slate-700 truncate">{doc.stage_name || doc.live_stage_name || "—"}</div>
                </div>
                <div className="bg-slate-50 rounded px-2 py-1.5">
                  <div className="text-[10px] uppercase text-slate-400 font-semibold">Assicurazione</div>
                  <div className="text-slate-700 truncate">{doc.assicurazione_name || "—"}</div>
                </div>
                <div className="bg-slate-50 rounded px-2 py-1.5">
                  <div className="text-[10px] uppercase text-slate-400 font-semibold">Medico</div>
                  <div className="text-slate-700 truncate">{doc.assigned_user_name || doc.medico_titolare || "—"}</div>
                </div>
              </div>

              {/* Piano di Cura */}
              <div>
                <div className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-2">
                  Trattamenti ({piano.length} {piano.length === 1 ? "gruppo" : "gruppi"})
                </div>
                {piano.length === 0 ? (
                  <div className="text-sm text-slate-400 italic">Nessun trattamento registrato.</div>
                ) : (
                  <div className="space-y-2">
                    {piano.map((g, gi) => {
                      const denti = g.denti || [];
                      const label = g.fullMouth
                        ? "Tutta la bocca"
                        : (denti.length > 0 ? denti.join(", ") : "—");
                      return (
                        <div key={g.id ?? `group-${gi}`} className="border border-slate-200 rounded-lg overflow-hidden">
                          <div className="bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-700 flex items-center justify-between gap-2">
                            <span>Gruppo {gi + 1} · {label}</span>
                            {g.indirettoTarget && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 uppercase">
                                Indiretti {g.indirettoTarget}
                              </span>
                            )}
                          </div>
                          <table className="w-full text-xs">
                            <thead className="bg-slate-50 text-slate-500">
                              <tr>
                                <th className="text-left px-3 py-1 font-semibold">Codice</th>
                                <th className="text-left px-3 py-1 font-semibold">Trattamento</th>
                                <th className="text-right px-3 py-1 font-semibold">Unitario</th>
                                <th className="text-right px-3 py-1 font-semibold">×</th>
                                <th className="text-right px-3 py-1 font-semibold">Totale</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(g.trattamenti || []).map((t, ti) => {
                                const mult = (g.fullMouth ? 1 : denti.length) || 1;
                                const unit = Number(t.euro || 0);
                                const rowTotal = unit * mult;
                                return (
                                  <tr key={`${g.id ?? gi}-${t.treatmentCode || ti}`} className="border-t border-slate-100">
                                    <td className="px-3 py-1 font-mono text-slate-700">{t.treatmentCode || "—"}</td>
                                    <td className="px-3 py-1 text-slate-700 truncate max-w-[260px]" title={t.treatmentName}>{t.treatmentName || "—"}</td>
                                    <td className="px-3 py-1 text-right tabular-nums text-slate-700">{fmtEuro(unit)}</td>
                                    <td className="px-3 py-1 text-right tabular-nums text-slate-500">{mult}</td>
                                    <td className="px-3 py-1 text-right tabular-nums font-semibold text-slate-800">{fmtEuro(rowTotal)}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Totali */}
              <div className="border-t pt-3 space-y-1 text-sm">
                {totalBase > 0 && totalBase !== total && (
                  <div className="flex justify-between text-slate-600">
                    <span>Totale Listino BDD (privato)</span>
                    <span className="tabular-nums">{fmtEuro(totalBase)}</span>
                  </div>
                )}
                <div className="flex justify-between font-bold text-slate-900">
                  <span>Totale piano (Assicurativo)</span>
                  <span className="tabular-nums">{fmtEuro(total)}</span>
                </div>
              </div>

              {/* Note */}
              {doc.notes && (
                <div>
                  <div className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-1">Note</div>
                  <div className="text-xs text-slate-700 bg-slate-50 border border-slate-200 rounded p-2 whitespace-pre-wrap">
                    {doc.notes}
                  </div>
                </div>
              )}

              {doc.is_deleted && (
                <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                  Questa opportunità risulta cancellata su GHL. Stai visualizzando l&apos;ultima copia locale.
                </div>
              )}
            </>
          )}
        </div>

        <DialogFooter className="px-5 py-3 border-t bg-slate-50">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="piano-cura-preview-close">
            Chiudi
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AppointmentRow({ appt, localStatus, hasStudio, onCallPatient, onStageMoved, doctorFilter = "", showNoTransitBadge = true }) {
  const navigate = useNavigate();
  const startTime = appt.startTime
    ? new Date(appt.startTime).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })
    : "--:--";

  const isInStudio = localStatus === "instudio";
  const opps = appt.open_opportunities || [];
  const hasOpps = appt.has_open_opportunity && opps.length > 0;

  // Pre-fill helper for the "Piano di Cura" shortcut buttons. Carries the
  // patient context (GHL contact id + phone + email) and the doctor of the
  // appointment (preferred over the optional header filter) so the target
  // page can pre-populate the matching fields without any extra fetch.
  // Empty fields are omitted from the URL.
  const buildPianoCuraHref = (path) => {
    const params = new URLSearchParams();
    if (appt.contactId) params.set("contactId", appt.contactId);
    if (appt.title) params.set("patientName", appt.title);
    if (appt.phone) params.set("phone", appt.phone);
    if (appt.email) params.set("email", appt.email);
    // The appointment's own doctor wins over the page-level filter so the
    // form lands on the right medico even when the operator hasn't filtered
    // the list by doctor.
    const doctorName = appt.doctor_name || doctorFilter || "";
    if (doctorName) params.set("doctorName", doctorName);
    if (appt.assigned_user_id) params.set("doctorUserId", appt.assigned_user_id);
    const qs = params.toString();
    return qs ? `${path}?${qs}` : path;
  };

  return (
    <div
      className={`flex flex-col sm:grid sm:grid-cols-[68px_220px_minmax(0,1fr)_200px_120px] items-stretch sm:items-center gap-3 sm:gap-4 px-3 sm:px-4 py-3 rounded-xl border-2 transition-all ${
        isInStudio ? "bg-emerald-50 border-emerald-300" : "bg-white border-slate-200 hover:border-slate-300"
      }`}
      data-testid={`appointment-${appt.id}`}
    >
      {/* MOBILE header (ora + paziente + medico inline) — visibile solo
          su mobile, su desktop l'informazione vive nelle colonne separate. */}
      <div className="flex sm:hidden items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 mb-1">
            <Clock className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
            <span className="text-sm font-bold text-slate-800 tabular-nums">{startTime}</span>
            <span className="text-slate-300 mx-1">·</span>
            <Stethoscope className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
            <span className="text-xs font-medium text-slate-600 truncate">{appt.doctor_name || "—"}</span>
          </div>
          <div className="text-sm font-semibold text-slate-800 truncate" data-testid={`patient-mob-${appt.id}`}>
            {appt.has_phone_task && (
              <span
                className="inline-flex items-center gap-1 mr-1.5 px-1.5 py-0.5 rounded-md bg-red-100 text-red-700 border border-red-300 text-[10px] font-bold uppercase tracking-wide"
                title="Task GHL aperta: messaggio WA di backup + email — verificare numero di telefono"
                data-testid={`phone-task-tag-mob-${appt.id}`}
              >
                <Phone className="w-3 h-3" />
                Controllare telefono
              </span>
            )}
            {appt.title || "—"}
          </div>
        </div>
      </div>

      {/* 1. Ora — desktop only (mobile shown in header above) */}
      <div className="hidden sm:flex items-center gap-1.5">
        <Clock className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
        <span className="text-sm font-bold text-slate-800 tabular-nums">{startTime}</span>
      </div>

      {/* 2. Paziente: nome (desktop only) + icone link/foto/piani cura
          (sempre visibili — su mobile sotto al titolo). */}
      <div className="min-w-0">
        <div className="hidden sm:block min-w-0">
          {appt.has_phone_task && (
            <div
              className="inline-flex items-center gap-1 mb-1 px-1.5 py-0.5 rounded-md bg-red-100 text-red-700 border border-red-300 text-[10px] font-bold uppercase tracking-wide"
              title="Task GHL aperta: messaggio WA di backup + email — verificare numero di telefono"
              data-testid={`phone-task-tag-${appt.id}`}
            >
              <Phone className="w-3 h-3" />
              Controllare telefono
            </div>
          )}
          <div className="text-sm font-semibold text-slate-800 truncate" data-testid={`patient-${appt.id}`}>
            {appt.title || "—"}
          </div>
        </div>
        <div className="flex items-center gap-1.5 sm:mt-1.5">
          {hasOpps && appt.ghl_contact_url && (
            <a
              href={appt.ghl_contact_url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              title="Apri opportunità in GHL"
              data-testid={`open-opp-${appt.id}`}
              className="inline-flex items-center justify-center w-7 h-7 rounded-md bg-indigo-50 text-indigo-600 hover:bg-indigo-100 hover:text-indigo-800 transition-colors flex-shrink-0"
            >
              <ExternalLink className="w-4 h-4" />
            </a>
          )}
          <a
            href={buildPianoCuraHref("/piano-cura-clinico")}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            title="Apri Piano di Cura — Reparto Clinico"
            data-testid={`open-pc-clinico-${appt.id}`}
            className="inline-flex items-center justify-center w-7 h-7 rounded-md bg-teal-50 text-teal-600 hover:bg-teal-100 hover:text-teal-800 transition-colors flex-shrink-0"
          >
            <ClipboardList className="w-4 h-4" />
          </a>
          <a
            href={buildPianoCuraHref("/piano-cura-ortodonzia")}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            title="Apri Piano di Cura — Ortodonzia"
            data-testid={`open-pc-ortodonzia-${appt.id}`}
            className="inline-flex items-center justify-center w-7 h-7 rounded-md bg-fuchsia-50 text-fuchsia-600 hover:bg-fuchsia-100 hover:text-fuchsia-800 transition-colors flex-shrink-0"
          >
            <Smile className="w-4 h-4" />
          </a>
        </div>
      </div>

      {/* 3. Fase Pipeline — mobile: full width, desktop: centrata 480px */}
      <div className="w-full sm:w-[480px] sm:justify-self-center flex flex-col gap-1.5 items-stretch">
        {hasOpps ? (
          opps.map((opp) => (
            <StageSelector
              key={opp.id}
              opportunity={opp}
              patientName={appt.title}
              onConfirmed={onStageMoved}
              showNoTransitBadge={showNoTransitBadge}
            />
          ))
        ) : (
          <div className="text-[11px] text-slate-300 italic text-center py-2 hidden sm:block">—</div>
        )}
        {/* Badge Promemoria: mostra il mese dell'ultima opp aperta nelle
            pipeline Promemoria Reparto Clinico / Promemoria Ortodonzia.
            Click → naviga alla pagina Promemoria con highlight sull'opp. */}
        {(appt.reminders?.clinico || appt.reminders?.orto) && (
          <div className="flex flex-wrap items-center justify-center gap-1 mt-0.5">
            {appt.reminders?.clinico && (
              <ReminderBadge
                kind="clinico"
                reminder={appt.reminders.clinico}
                onOpen={() =>
                  navigate(`/promemoria-clinico?opp=${encodeURIComponent(appt.reminders.clinico.opportunity_id)}`)
                }
              />
            )}
            {appt.reminders?.orto && (
              <ReminderBadge
                kind="orto"
                reminder={appt.reminders.orto}
                onOpen={() =>
                  navigate(`/promemoria-ortodonzia?opp=${encodeURIComponent(appt.reminders.orto.opportunity_id)}`)
                }
              />
            )}
          </div>
        )}
      </div>

      {/* 4. Medico — desktop only (mobile shown in header above) */}
      <div className="hidden sm:flex items-center gap-1.5 min-w-0">
        <Stethoscope className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
        <span className="text-xs sm:text-sm font-medium text-slate-600 truncate">
          {appt.doctor_name || "—"}
        </span>
      </div>

      {/* 5. Azione */}
      <div className="flex sm:justify-end">
        <Button
          size="sm"
          data-testid={`btn-instudio-${appt.id}`}
          disabled={!hasStudio && !isInStudio}
          title={!hasStudio && !isInStudio ? "Seleziona medico e studio nell'header" : ""}
          className={`h-9 w-full px-3 text-xs font-semibold transition-all ${
            isInStudio
              ? "bg-emerald-600 text-white hover:bg-emerald-700"
              : hasStudio
                ? "bg-white border border-emerald-300 text-emerald-700 hover:bg-emerald-50 hover:border-emerald-400"
                : "bg-slate-100 border border-slate-200 text-slate-300 cursor-not-allowed"
          }`}
          onClick={() => onCallPatient(appt, isInStudio ? "remove" : "add")}
        >
          <Phone className="w-3.5 h-3.5 mr-1" />
          {isInStudio ? "Rimuovi" : "In Studio"}
        </Button>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// ReminderBadge: pill cliccabile mostrato nella colonna centrale.
// Aspetto allineato visivamente agli StageSelector (h-9, rounded-lg,
// border, full-width). Click → navigate alla pagina Promemoria.
// ────────────────────────────────────────────────────────────────────
function ReminderBadge({ kind, reminder, onOpen }) {
  const isClinico = kind === "clinico";
  const label = isClinico ? "Promemoria Clinico" : "Promemoria Orto";
  const cls = isClinico
    ? "bg-teal-50 text-teal-700 border-teal-200 hover:bg-teal-100"
    : "bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200 hover:bg-fuchsia-100";
  const stage = reminder?.stage_name || "—";
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onOpen(); }}
      title={`${label}: ${stage} · Click per aprire la pagina Promemoria`}
      data-testid={`reminder-${kind}-${reminder?.opportunity_id || "x"}`}
      className={`h-9 px-3 inline-flex items-center gap-2 rounded-lg border text-xs font-semibold w-full min-w-0 transition-colors ${cls}`}
    >
      <CalendarIcon className="w-3.5 h-3.5 flex-shrink-0" />
      <span className="truncate">
        {label} · <span className="font-bold">{stage}</span>
      </span>
    </button>
  );
}

export default function ChiamaPazientePage() {
  const { user: authUser } = useAuth();
  const isAdminUser = (authUser?.role || "") === "admin";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statuses, setStatuses] = useState({}); // apptId -> "instudio"
  const [studios, setStudios] = useState({}); // apptId -> "Studio 1" (persisted per appt)
  const [lastRefresh, setLastRefresh] = useState(null);
  const [refreshingMedici, setRefreshingMedici] = useState(false);

  // Header filters (persisted in localStorage)
  const [doctorFilter, setDoctorFilter] = useState(() => localStorage.getItem("cp_doctorFilter") || "");
  const [currentStudio, setCurrentStudio] = useState(() => localStorage.getItem("cp_currentStudio") || "");
  // Selected day (defaults to today, NOT persisted — user always opens on today)
  const todayLocal = useMemo(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  }, []);
  const [selectedDate, setSelectedDate] = useState(todayLocal);
  const isToday = selectedDate === todayLocal;
  // Badge "Senza cambio stage" — regola UI:
  //   • Giorni passati: sempre visibile (ormai lo stage non cambierà più).
  //   • Oggi: visibile SOLO dopo le 19:00 ora Roma. Prima è ancora presto e
  //     lo stage potrebbe transitare prima della chiusura studio.
  //   • Giorni futuri: nessuno transito ancora possibile → niente badge.
  // Ricalcoliamo `showNoTransitBadge` ogni minuto (re-render del componente
  // su intervallo del polling già esistente).
  const showNoTransitBadge = useMemo(() => {
    if (selectedDate < todayLocal) return true;   // giorno passato
    if (selectedDate > todayLocal) return false;  // giorno futuro
    // Giorno odierno → controlla l'ora attuale (Europe/Rome)
    const now = new Date();
    const romeHour = parseInt(
      new Intl.DateTimeFormat("it-IT", {
        timeZone: "Europe/Rome",
        hour: "2-digit",
        hour12: false,
      }).format(now),
      10,
    );
    return romeHour >= 19;
    // `lastRefresh` è incluso intenzionalmente come trigger: ogni volta
    // che il polling aggiorna `lastRefresh` (≈ ogni minuto), questo
    // useMemo ricalcola `romeHour` da `new Date()` per gestire il
    // passaggio delle 19:00. Senza `lastRefresh` il badge resterebbe
    // "stale" finché non cambia `selectedDate` o `todayLocal`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, todayLocal, lastRefresh]);

  const intervalRef = useRef(null);
  const lastFetchRef = useRef(0);
  const inFlightRef = useRef(false);

  // Persist header selections
  useEffect(() => {
    localStorage.setItem("cp_doctorFilter", doctorFilter);
  }, [doctorFilter]);
  useEffect(() => {
    localStorage.setItem("cp_currentStudio", currentStudio);
  }, [currentStudio]);

  const fetchAppointments = useCallback(async (showLoading = true) => {
    // Guardia anti-sovrapposizione: con GHL down la chiamata può durare
    // fino a 45s (stale-while-revalidate). L'auto-refresh accelerato a 15s
    // non deve accodare richieste su richieste → salta se già in corso.
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    if (showLoading) setLoading(true);
    try {
      const params = {};
      if (selectedDate) params.date = selectedDate;
      const { data: resp } = await axios.get(`${API}/appointments/today`, { params, withCredentials: true });
      setData(resp);
      lastFetchRef.current = Date.now();
      setLastRefresh(new Date());
    } catch (err) {
      console.error("fetchAppointments:", err);
    } finally {
      setLoading(false);
      inFlightRef.current = false;
    }
  }, [selectedDate]);

  // Aggiornamento ottimistico dopo lo spostamento di uno stage da
  // `StageSelector`: mostriamo subito il nuovo stage nella riga (senza
  // attendere il prossimo polling di 60s) e in parallelo facciamo un
  // refetch in background per allinearci ai dati reali di GHL (in caso di
  // downstream_stages cambiati, opp chiusa, ecc.).
  //
  // 24/06/2026: con `pipeline_stages` esposto dal backend, il dropdown
  // si auto-ricalcola in base al nuovo `current_stage_id` → non serve
  // più filtrare manualmente `downstream_stages` (che era una fonte di
  // race condition durante il 1s window prima del refetch).
  const handleStageMoved = useCallback((oppId, stageId, stageName) => {
    setData((prev) => {
      if (!prev || !Array.isArray(prev.appointments)) return prev;
      const nextAppts = prev.appointments.map((appt) => {
        if (!appt?.open_opportunities) return appt;
        let touched = false;
        const nextOpps = appt.open_opportunities.map((op) => {
          if (op?.id !== oppId) return op;
          touched = true;
          // Recalculate current_stage_position from pipeline_stages so
          // permission/sort logic stays consistent without waiting for
          // the background refetch.
          const newPos = Array.isArray(op.pipeline_stages)
            ? (op.pipeline_stages.find((s) => s.id === stageId)?.position ?? null)
            : null;
          return {
            ...op,
            current_stage_id: stageId,
            current_stage_name: stageName,
            current_stage_position: newPos,
            // Legacy field: keep it in sync per backward-compat
            downstream_stages: (op.downstream_stages || []).filter((s) => s.id !== stageId),
          };
        });
        return touched ? { ...appt, open_opportunities: nextOpps } : appt;
      });
      return { ...prev, appointments: nextAppts };
    });
    // Background refetch (no spinner) to reconcile with GHL within ~1s.
    fetchAppointments(false);
  }, [fetchAppointments]);

  // Quando il backend serve uno snapshot stale (GHL down), accorciamo
  // l'intervallo di polling da 60s a 15s così la pagina si riallinea
  // appena GHL torna raggiungibile, senza attendere il minuto pieno.
  const isStale = !!data?.stale;

  useEffect(() => {
    fetchAppointments();
    // Auto-refresh only when looking at TODAY — for other days it makes no
    // sense (data is historical or future, won't change live).
    if (isToday) {
      const refreshMs = isStale ? 15000 : 60000;
      intervalRef.current = setInterval(() => fetchAppointments(false), refreshMs);
    }

    // Wake-up refresh: browsers throttle setInterval when the tab is in
    // background, so when the user comes back to the page we refetch
    // immediately if the data is stale (last fetch > 30s ago). This avoids
    // showing stale data after the user comes back from another tab.
    const handleVisibility = () => {
      if (document.visibilityState !== "visible") return;
      if (!isToday) return;
      const last = lastFetchRef.current || 0;
      if (Date.now() - last > 30000) {
        fetchAppointments(false);
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener("focus", handleVisibility);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("focus", handleVisibility);
    };
  }, [fetchAppointments, isToday, isStale]);

  // Load saved statuses/studios from backend (one-shot al mount).
  // Effect dedicato per evitare il blocking lint "set-state-in-effect"
  // sulla principale.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data: resp } = await axios.get(
          `${API}/appointments/monitor/patients-list`,
          { withCredentials: true },
        );
        if (cancelled) return;
        const savedStatuses = {};
        const savedStudios = {};
        for (const p of resp.in_studio || []) {
          savedStatuses[p.appointment_id] = "instudio";
          if (p.studio_number) savedStudios[p.appointment_id] = p.studio_number;
        }
        if (Object.keys(savedStatuses).length > 0) setStatuses(savedStatuses);
        if (Object.keys(savedStudios).length > 0) setStudios(savedStudios);
      } catch (err) {
        console.debug("patients-list load failed:", err?.message || err);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);  const appointments = useMemo(
    () => (Array.isArray(data?.appointments) ? data.appointments : []),
    [data]
  );

  // Unique doctors list for filter dropdown.
  // 17/06/2026 — Preferisce la lista COMPLETA `all_doctors` esposta dal
  // backend (cache utenti GHL) per evitare il bug "Tutti i medici (0)"
  // quando il giorno selezionato non ha ancora appuntamenti. Fallback:
  // deriva i medici dagli `appointments` (caso storico).
  // Filtra fuori utenti non-medici (segreteria, marketing, studi non
  // assegnati a un medico) mostrando solo quelli che iniziano con "Dott."
  // OPPURE che hanno almeno un appuntamento oggi (caso eccezione).
  const doctorList = useMemo(() => {
    const apptDoctors = new Set();
    for (const a of appointments) {
      if (a.doctor_name) apptDoctors.add(a.doctor_name);
    }
    const backendList = Array.isArray(data?.all_doctors) ? data.all_doctors : [];
    const merged = new Set([...apptDoctors, ...backendList]);
    return [...merged]
      .filter((name) => /^Dott\./i.test(name) || apptDoctors.has(name))
      .sort();
  }, [appointments, data]);

  // Auto-reset del filtro medico se il valore salvato in localStorage non
  // appartiene più alla lista medici reale (caso: la cache utenti GHL era
  // bloccata su "Sconosciuto" e l'operatore aveva selezionato quella voce;
  // dopo il refresh della cache "Sconosciuto" sparisce ma il filtro resta
  // attivo → 0 risultati). Aspettiamo che siano stati caricati appt validi
  // PRIMA di resettare, altrimenti azzeriamo per niente all'avvio.
  useEffect(() => {
    if (
      doctorFilter &&
      appointments.length > 0 &&
      doctorList.length > 0 &&
      !doctorList.includes(doctorFilter)
    ) {
      console.info(
        `[ChiamaPaziente] Reset filtro medico "${doctorFilter}" — `
        + `non presente fra ${doctorList.length} medici reali`
      );
      setDoctorFilter("");
    }
  }, [doctorFilter, doctorList, appointments.length]);

  // Filtered appointments
  const filteredAppointments = useMemo(() => {
    if (!doctorFilter) return appointments;
    return appointments.filter((a) => a.doctor_name === doctorFilter);
  }, [appointments, doctorFilter]);

  // 17/06/2026 — Admin-only: forza il refresh della cache utenti GHL
  // (in-memory). Utile quando il dropdown medici è bloccato su 0 perché
  // la cache è stata popolata a vuoto (rate limit, network blip).
  const handleRefreshMedici = useCallback(async () => {
    setRefreshingMedici(true);
    try {
      // Refresh in parallelo: cache medici (calendari/users) + cache
      // assistenti (contacts/search). Entrambe usano cache lato server
      // che possono restare stale → refresh manuale forza il rebuild.
      // BUG FIX 17/06/2026: prima il bottone toccava solo i medici,
      // lasciando il dropdown assistenti potenzialmente stale → bug
      // "salta l'assistente di studio" al riaprire le schede.
      const [docRes] = await Promise.all([
        axios.post(`${API}/appointments/users-refresh`, {}, { withCredentials: true }),
        axios.get(`${API}/ghl/assistants?force_refresh=1`, { withCredentials: true }),
      ]);
      toast.success(`Cache medici + assistenti ricaricata: ${docRes?.data?.users_loaded ?? 0} medici caricati da GHL`);
      // Ricarica anche gli appuntamenti per applicare i nuovi nomi
      fetchAppointments(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nel refresh della cache");
    } finally {
      setRefreshingMedici(false);
    }
  }, [fetchAppointments]);

  const handleCallPatient = (appt, action) => {
    const newStatus = action === "add" ? "instudio" : "default";
    const studioForThis = action === "add" ? currentStudio : studios[appt.id] || currentStudio;

    setStatuses((prev) => ({ ...prev, [appt.id]: newStatus }));
    if (action === "add" && studioForThis) {
      setStudios((prev) => ({ ...prev, [appt.id]: studioForThis }));
    }

    axios
      .post(
        `${API}/appointments/monitor/set-status`,
        {
          appointment_id: appt.id,
          patient_name: appt.title || "",
          doctor_name: appt.doctor_name || "",
          status: "instudio",
          action: action, // "add" | "remove"
          start_time: appt.startTime || "",
          studio_number: studioForThis || "",
          contact_id: appt.contactId || "",
        },
        { withCredentials: true }
      )
      .catch((err) => console.error("monitor sync:", err));
  };

  const totalAppts = appointments.length;
  const filteredCount = filteredAppointments.length;
  const inStudioCount = Object.values(statuses).filter((s) => s === "instudio").length;

  return (
    <div className="min-h-screen bg-gray-100 bg-grid-light pt-[calc(7px+3.5rem+13px)] pb-10 px-4 lg:px-[100px]">
      <div className="w-full">
        <PageHeader
          title="Chiama Paziente"
          subtitle={
            <>
              {isToday ? "Appuntamenti di oggi" : "Appuntamenti del"}{" "}&mdash;{" "}
              {data?.date
                ? new Date(data.date + "T00:00:00").toLocaleDateString("it-IT", {
                    weekday: "long",
                    day: "numeric",
                    month: "long",
                  })
                : ""}
            </>
          }
          actions={
            <div className="flex items-center gap-2">
              {isAdminUser && (
                <button
                  type="button"
                  onClick={handleRefreshMedici}
                  disabled={refreshingMedici}
                  data-testid="refresh-medici-btn"
                  title="Admin: ricarica le cache utenti GHL (medici + assistenti)"
                  aria-label="Ricarica cache medici e assistenti"
                  className="inline-flex items-center gap-2 px-3 h-10 rounded-xl bg-amber-500/20 hover:bg-amber-500/30 border border-amber-300/40 text-white text-xs font-semibold transition-colors backdrop-blur-sm disabled:opacity-50"
                >
                  {refreshingMedici ? <Loader2 className="w-4 h-4 animate-spin" /> : <Stethoscope className="w-4 h-4" />}
                  <span className="hidden sm:inline">Ricarica utenti GHL</span>
                </button>
              )}
              <button
                type="button"
                onClick={() => fetchAppointments(true)}
                data-testid="refresh-btn"
                title="Aggiorna appuntamenti"
                aria-label="Aggiorna"
                className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-white/10 hover:bg-white/20 border border-white/20 text-white transition-colors backdrop-blur-sm"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
              </button>
            </div>
          }
        />
      </div>
      <div className="w-full mb-6 -mt-8 relative z-10">
        {/* Banner "dati non aggiornati" — appare quando il backend serve
            uno snapshot stale a causa di GHL rate-limited / down. */}
        {data?.stale && (
          <div
            className="mb-3 rounded-xl border border-amber-300 bg-amber-50 text-amber-900 px-4 py-2.5 text-sm flex items-start gap-2"
            data-testid="stale-data-banner"
          >
            <svg className="w-4 h-4 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
            </svg>
            <div className="flex-1">
              <div className="font-semibold">Dati non aggiornati</div>
              <div className="text-xs opacity-80">
                GoHighLevel non risponde in questo momento — stai vedendo l'ultima copia salvata
                {data.stale_since && (
                  <>
                    {" "}del{" "}
                    {new Date(data.stale_since).toLocaleString("it-IT", {
                      day: "2-digit", month: "2-digit", year: "numeric",
                      hour: "2-digit", minute: "2-digit",
                    })}
                  </>
                )}.{" "}
                <span className="inline-flex items-center gap-1 font-medium">
                  <RefreshCw className="w-3 h-3 animate-spin" />
                  Nuovo tentativo automatico ogni 15 secondi…
                </span>
              </div>
            </div>
            <button
              type="button"
              onClick={() => fetchAppointments(true)}
              className="text-xs font-semibold text-amber-700 hover:text-amber-900 underline flex-shrink-0"
              data-testid="stale-retry-btn"
            >
              Riprova
            </button>
          </div>
        )}
        {/* Header filters card */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-4 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
          <div className="flex flex-col sm:flex-row gap-3 flex-1">
            {/* Date picker — defaults to today, lets the user open any other day */}
            <div className="flex items-center gap-1.5 flex-1 min-w-0">
              <CalendarIcon className="w-4 h-4 text-slate-400 flex-shrink-0" />
              {/* ← / → frecce per scorrere un giorno alla volta. Parse-as-local
                  per evitare i drift di timezone tipici di `new Date(YYYY-MM-DD)`
                  (che viene interpretato come UTC). */}
              <button
                type="button"
                onClick={() => {
                  const [y, m, d] = selectedDate.split("-").map(Number);
                  const dt = new Date(y, m - 1, d);
                  dt.setDate(dt.getDate() - 1);
                  const nd = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
                  setSelectedDate(nd);
                }}
                data-testid="cp-prev-day"
                title="Giorno precedente"
                aria-label="Giorno precedente"
                className="h-10 w-9 flex items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 hover:border-blue-300 hover:text-blue-600 transition-colors flex-shrink-0"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value || todayLocal)}
                data-testid="cp-date-picker"
                className={`h-10 w-full text-sm border rounded-lg px-3 bg-white focus:outline-none focus:border-blue-400 ${
                  !isToday ? "border-amber-300 text-amber-700 font-semibold" : "border-slate-200 text-slate-700"
                }`}
                title="Seleziona giorno"
              />
              <button
                type="button"
                onClick={() => {
                  const [y, m, d] = selectedDate.split("-").map(Number);
                  const dt = new Date(y, m - 1, d);
                  dt.setDate(dt.getDate() + 1);
                  const nd = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
                  setSelectedDate(nd);
                }}
                data-testid="cp-next-day"
                title="Giorno successivo"
                aria-label="Giorno successivo"
                className="h-10 w-9 flex items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 hover:border-blue-300 hover:text-blue-600 transition-colors flex-shrink-0"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
              {!isToday && (
                <button
                  type="button"
                  onClick={() => setSelectedDate(todayLocal)}
                  data-testid="cp-back-to-today"
                  className="text-[11px] font-semibold text-blue-600 hover:underline whitespace-nowrap"
                >
                  Oggi
                </button>
              )}
            </div>
            {/* Doctor filter */}
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <Filter className="w-4 h-4 text-slate-400 flex-shrink-0" />
              <select
                value={doctorFilter}
                onChange={(e) => setDoctorFilter(e.target.value)}
                data-testid="doctor-filter-select"
                className="h-10 w-full text-sm border border-slate-200 rounded-lg px-3 bg-white text-slate-700 focus:outline-none focus:border-blue-400"
              >
                <option value="">Tutti i medici ({doctorList.length})</option>
                {doctorList.map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </select>
            </div>

            {/* Current studio */}
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <Building2 className="w-4 h-4 text-slate-400 flex-shrink-0" />
              <select
                value={currentStudio}
                onChange={(e) => setCurrentStudio(e.target.value)}
                data-testid="current-studio-select"
                className={`h-10 w-full text-sm border rounded-lg px-3 bg-white focus:outline-none focus:border-blue-400 ${
                  currentStudio ? "border-emerald-300 text-emerald-700 font-semibold" : "border-slate-200 text-slate-700"
                }`}
              >
                <option value="">Seleziona il tuo studio…</option>
                {STUDIOS.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Badges */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <div className="flex items-center gap-1.5 bg-slate-100 rounded-lg px-3 py-1.5">
              <Users className="w-3.5 h-3.5 text-slate-500" />
              <span className="text-xs font-semibold text-slate-600" data-testid="total-count">
                {doctorFilter ? `${filteredCount}/${totalAppts}` : totalAppts}
              </span>
            </div>
            {inStudioCount > 0 && (
              <div className="flex items-center gap-1.5 bg-emerald-100 rounded-lg px-3 py-1.5">
                <Phone className="w-3.5 h-3.5 text-emerald-600" />
                <span className="text-xs font-semibold text-emerald-700">{inStudioCount} in studio</span>
              </div>
            )}
          </div>
        </div>

        {lastRefresh && (
          <p className="text-slate-400 text-xs mt-2">
            Ultimo aggiornamento: {lastRefresh.toLocaleTimeString("it-IT")}
            {isToday ? (isStale ? " (ritento ogni 15s…)" : " (auto-refresh ogni 60s)") : (
              <span className="ml-2 inline-flex items-center gap-1 text-amber-700 font-semibold">
                · stai consultando un altro giorno (auto-refresh disattivato)
              </span>
            )}
            {!(currentStudio && doctorFilter) && appointments.length > 0 && isToday && (
              <span className="text-amber-600 ml-2 font-medium">
                ⚠ Seleziona medico e studio nell&apos;header per poter chiamare i pazienti
              </span>
            )}
          </p>
        )}
      </div>

      <div className="w-full">
        {loading && !data ? (
          <div className="bg-white rounded-3xl shadow-md p-12 text-center">
            <RefreshCw className="w-8 h-8 text-slate-300 animate-spin mx-auto mb-3" />
            <p className="text-slate-400">Caricamento appuntamenti...</p>
          </div>
        ) : appointments.length === 0 ? (
          <div className="bg-white rounded-3xl shadow-md p-12 text-center">
            <Users className="w-12 h-12 text-slate-200 mx-auto mb-3" />
            <p className="text-slate-400 text-lg">Nessun appuntamento per oggi</p>
          </div>
        ) : filteredAppointments.length === 0 ? (
          <div className="bg-white rounded-3xl shadow-md p-10 text-center">
            <Filter className="w-10 h-10 text-slate-200 mx-auto mb-3" />
            <p className="text-slate-400">Nessun appuntamento per {doctorFilter}</p>
            <button
              className="text-xs text-blue-600 mt-2 hover:underline"
              onClick={() => setDoctorFilter("")}
            >
              Mostra tutti i medici
            </button>
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-3 sm:p-4 space-y-2">
            {/* Header row — must match AppointmentRow's grid template exactly */}
            <div className="hidden sm:grid grid-cols-[68px_220px_minmax(0,1fr)_200px_120px] gap-4 px-4 pb-2 border-b border-slate-100 text-[10px] font-bold uppercase tracking-wider text-slate-400">
              <div>Ora</div>
              <div>Paziente</div>
              <div></div>
              <div>Medico</div>
              <div></div>
            </div>

            {filteredAppointments.map((appt) => (
              <AppointmentRow
                key={appt.id}
                appt={appt}
                localStatus={statuses[appt.id] || "default"}
                hasStudio={!!currentStudio && !!doctorFilter}
                onCallPatient={handleCallPatient}
                onStageMoved={handleStageMoved}
                doctorFilter={doctorFilter}
                showNoTransitBadge={showNoTransitBadge}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
